import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

const LaserBorderImage = ({ src }) => {
    return (
        <div className="relative group p-[2px] rounded-xl overflow-hidden bg-fanteex-purple/50">
            {/* The Static Thick Border */}
            <div className="absolute inset-0 border-2 border-[#5DFDCB]/30 rounded-xl z-20 pointer-events-none" />
            
            {/* The Moving Laser "Snake" */}
            {/* We create a conic gradient that spins, masked to the border area? 
                Actually, simpler is 4 divs animating along the edges for a crisp "laser" look */}
            
            {/* Top Laser */}
            <div className="absolute top-0 left-0 w-full h-[4px] bg-gradient-to-r from-transparent via-[#5DFDCB] to-transparent -translate-x-full animate-[laser-x_2s_linear_infinite] z-30 shadow-[0_0_10px_#5DFDCB]" />
            {/* Right Laser */}
            <div className="absolute top-0 right-0 w-[4px] h-full bg-gradient-to-b from-transparent via-[#5DFDCB] to-transparent -translate-y-full animate-[laser-y_2s_linear_infinite_0.5s] z-30 shadow-[0_0_10px_#5DFDCB]" />
            {/* Bottom Laser */}
            <div className="absolute bottom-0 right-0 w-full h-[4px] bg-gradient-to-l from-transparent via-[#5DFDCB] to-transparent translate-x-full animate-[laser-x_2s_linear_infinite_1s] z-30 shadow-[0_0_10px_#5DFDCB]" />
            {/* Left Laser */}
            <div className="absolute bottom-0 left-0 w-[4px] h-full bg-gradient-to-t from-transparent via-[#5DFDCB] to-transparent translate-y-full animate-[laser-y_2s_linear_infinite_1.5s] z-30 shadow-[0_0_10px_#5DFDCB]" />

            {/* Inner Image */}
            <div className="relative z-10 rounded-[10px] overflow-hidden bg-black/50 backdrop-blur-sm">
                <img src={src} alt="App Interface" className="w-full h-auto object-cover transform transition-transform duration-700 group-hover:scale-105" />
                 {/* Scanline overlay */}
                <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,20,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-20 bg-[length:100%_2px,3px_100%] pointer-events-none" />
            </div>
        </div>
    );
};

const FeatureSection = ({ title, subtitle, description, imageSrc, align = "left", screenshots = [] }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"]
    });

    const y = useTransform(scrollYProgress, [0, 1], [100, -100]);
    const opacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], [0, 1, 1, 0]);
    const scale = useTransform(scrollYProgress, [0, 0.5], [0.8, 1]);

    return (
        <section ref={ref} className="min-h-screen flex flex-col items-center justify-center relative py-20 px-4 md:px-6 overflow-hidden z-20">
            {/* Background ambient glow specific to section */}
            <div className={`absolute top-1/2 ${align === 'left' ? 'left-[-20%]' : 'right-[-20%]'} w-[600px] h-[600px] bg-[#5DFDCB]/5 rounded-full blur-[120px] pointer-events-none`} />

            <div className="max-w-7xl w-full grid grid-cols-1 lg:grid-cols-2 gap-16 items-center z-10">
                {/* Content Side */}
                <motion.div
                    style={{ opacity }}
                    className={`flex flex-col gap-6 ${align === 'right' ? 'lg:order-2' : ''}`}
                >
                    <h2 className="text-5xl md:text-7xl font-display font-black text-white uppercase leading-[0.9]">
                        {title}
                    </h2>
                    <div className="overflow-hidden">
                         <motion.p
                            initial={{ y: 50 }}
                            whileInView={{ y: 0 }}
                            transition={{ duration: 0.8, ease: "circOut" }}
                            className="text-[#5DFDCB] font-display font-black text-2xl uppercase tracking-widest"
                        >
                            {subtitle}
                        </motion.p>
                    </div>
                    <p className="font-sans text-gray-300 text-lg md:text-xl leading-relaxed max-w-full lg:max-w-lg">
                        {description}
                    </p>
                    
                    {/* Decorative Line */}
                    <motion.div 
                        initial={{ width: 0 }}
                        whileInView={{ width: 100 }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="h-1 bg-fanteex-orange mt-4 shadow-[0_0_10px_#ff5e00]"
                    />
                </motion.div>

                {/* Visual Side */}
                <motion.div 
                    style={{ y, scale }}
                    className={`relative ${align === 'right' ? 'lg:order-1' : ''}`}
                >
                     {/* Decorative Elements behind screenshots */}
                     <div className="absolute top-[-20px] -right-10 w-20 h-20 border-t-2 border-r-2 border-[#5DFDCB]/30 rounded-tr-3xl" />
                     <div className="absolute -bottom-10 -left-10 w-20 h-20 border-b-2 border-l-2 border-[#5DFDCB]/30 rounded-bl-3xl" />

                    {/* Main Image/Screenshots */}
                    <div className="relative z-10 grid grid-cols-2 gap-6 perspective-1000">
                        {screenshots.map((src, index) => (
                            <motion.div
                                key={index}
                                whileHover={{ scale: 1.05, rotateY: 5, zIndex: 20 }}
                                className={`${index === 1 ? 'mt-16' : ''}`}
                            >
                                <LaserBorderImage src={src} />
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default FeatureSection;
